/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { collectionFiltersToFilterValues, viewIsLocked } from 'volta/models/collection-view';

import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import type { BufferedChangeset } from 'ember-changeset/types';
import type { IntlService } from 'ember-intl';
import type BvFlashService from 'volta/services/bv-flash';
import type SessionUserService from 'volta/services/session-user';
import type CollectionView from 'volta/models/collection-view';
import type { TEntityType } from 'volta/models/types/entity-types';
import type {
  ICollectionArgs,
  ICollectionFilter,
  ICollectionGroupOption,
  ICollectionProperty,
  ICollectionSortOption,
  ICollectionViewDefinition,
  ICollectionViewEditCmd,
  ICreateCollectionViewCmd,
  TCollectionType
} from 'volta/models/types/collection-view';
import type { EmberTableSort } from 'volta/components/bv-table';
import type Transition from '@ember/routing/transition';

const ICON_FROM_TYPE: Record<TCollectionType, string> = {
  TABLE: 'table-view',
  KANBAN: 'kanban-view',
  LIST: 'list',
  TIMELINE: 'timeline-view'
};

interface CollectionViewSignature {
  Args: {
    collectionDefinition: ICollectionViewDefinition;
    createViewChangeset?: BufferedChangeset & ICreateCollectionViewCmd;
    editViewChangeset?: BufferedChangeset & ICollectionViewEditCmd;
    enableGrouping?: boolean;
    filtersClassNames?: string;
    groupByOptions: ICollectionGroupOption[];
    hasUnsavedChanges: boolean;
    isLoading: boolean;
    metadata?: object;
    onCancelCreation: () => void;
    onComponentArgsChange?: (args: ICollectionArgs) => void;
    onCreate: () => Promise<void>;
    onDelete: () => Promise<void>;
    onDiscard?: () => void;
    onDuplicate: () => Promise<void>;
    onFilterValuesChange: (filter: TFilterValue, field: string) => void;
    onGroupByChange: (group: ICollectionGroupOption, checked: boolean) => void;
    onGroupByOptionsReorder: (groups: ICollectionGroupOption[]) => void;
    onInitCreate: () => void;
    onPropertyChange: (props: ICollectionProperty[]) => void;
    onQueryParamsChange: (element: HTMLElement, [queryParams]: [TQueryParams]) => void;
    onSave?: () => Promise<void>;
    onSearch: (values: TFilterValues, search: string) => void;
    onSortDirectionChange: (option: ICollectionSortOption) => void;
    onSortOptionsReorder: (options: ICollectionSortOption[]) => void;
    onSortSelectionChange: (option: ICollectionSortOption, checked: boolean) => void;
    queryParams: TQueryParams;
    selectedView?: CollectionView<ICollectionArgs>;
    selectedViewId: string;
    selectView: (viewId: string) => void;
    sendCollectionQueryParams: (isInitial?: boolean) => Transition | undefined;
    sortOptions: ICollectionSortOption[];
    sorts: EmberTableSort[];
    views?: CollectionView<ICollectionArgs>[];
  };
  Blocks: {
    filtersSecondary: [{ openEditModal: (section?: string) => void; filterValues: TFilterValues }];
  };
  Element: HTMLDivElement;
}

export default class CollectionViewComponent extends Component<CollectionViewSignature> {
  @service sessionUser!: SessionUserService;
  @service intl!: IntlService;
  @service bvFlash!: BvFlashService;

  @tracked settingsSection = 'general';
  @tracked editModalIsOpened = false;
  @tracked createModalIsOpened = false;
  @tracked confirmDeleteModalIsOpened = false;

  get orderedViews() {
    return this.args.views ?? [];
  }

  get isGroupMin() {
    const { groupMin } = this.args.collectionDefinition;
    if (!groupMin) {
      return false;
    }

    return this.args.groupByOptions.filter((option) => option.selected).length <= groupMin;
  }

  get filterValues() {
    const showForTags: ICollectionFilter[] = this.args.editViewChangeset?.get('filters') ?? [];
    return collectionFiltersToFilterValues(showForTags.filter((f) => f.property !== 'q'));
  }

  get viewIsLocked() {
    if (this.args.selectedView) {
      return viewIsLocked(this.args.selectedView, this.sessionUser.userId);
    }
    return false;
  }

  get selectedSortOptions() {
    return this.args.sortOptions.filter((o) => o.selected === true);
  }

  get filters(): ICollectionFilter[] {
    return this.args.editViewChangeset?.get('filters') ?? [];
  }

  @cached
  get properties(): ICollectionProperty[] {
    return this.args.editViewChangeset?.get('properties') ?? [];
  }

  get showedTagsFilters() {
    return this.filters.filter((f) => f.property !== 'q');
  }

  get user() {
    return this.sessionUser.user;
  }

  get userId() {
    return this.user!.id;
  }

  get sortDirectionGlyph() {
    const glyphs = [
      ...new Set(this.selectedSortOptions.map((o) => (o.asc ? 'arrow-down' : 'arrow-up')))
    ];
    return glyphs.length === 2 ? 'import-export' : glyphs[0];
  }

  get selectedGroups() {
    return this.args.groupByOptions.filter((g) => g.selected);
  }

  get otherGroups() {
    return this.args.groupByOptions.filter((g) => !g.selected);
  }

  // Actions

  @action
  handleSelectViewTab(view?: string) {
    if (view) {
      this.args.selectView(view);
    }
  }

  @action
  openCreateModal() {
    this.args.onInitCreate();
    this.settingsSection = 'general';
    this.createModalIsOpened = true;
  }

  @action
  closeCreateModal() {
    this.args.onCancelCreation();
    this.createModalIsOpened = false;
  }

  @action
  openEditModal(section: string = 'general') {
    this.settingsSection = section;
    this.editModalIsOpened = true;
  }

  get selectedViewIndex() {
    return this.orderedViews.findIndex((v) => v.idOrDefault === this.args.selectedViewId);
  }

  @action
  handleTabMove(collectionViewId: string, direction: 'left' | 'right') {
    const { orderedViews } = this;
    const cvIds = orderedViews.map((v) => v.idOrDefault);
    const index = cvIds.findIndex((id) => id === collectionViewId);
    const newIndex =
      direction === 'right' ? Math.min(index + 1, cvIds.length - 1) : Math.max(index - 1, 0);

    if (index !== newIndex) {
      [cvIds[index], cvIds[newIndex]] = [cvIds[newIndex]!, cvIds[index]!];
      this.sessionUser.setCollectionViewsOrder(
        this.args.collectionDefinition.resourceName.plural as TEntityType,
        cvIds
      );
    }
  }

  @action
  handleViewDeleted() {
    if (this.args.views?.length !== 1) {
      this.args.onDelete();
      this.editModalIsOpened = false;
      this.confirmDeleteModalIsOpened = false;
    }
  }

  @action
  handleGroupsDrag([options]: ICollectionGroupOption[][]) {
    if (options) {
      return this.args.onGroupByOptionsReorder?.(options);
    }
  }

  @action
  handleRemoveGroup(group: ICollectionGroupOption) {
    return this.args.onGroupByOptionsReorder?.(
      this.args.groupByOptions.filter((g) => g.value != group.value)
    );
  }

  @action
  async handleCopyLinkToView() {
    await navigator.clipboard.writeText(window.location.href);
    this.bvFlash.success(this.intl.t('copied'));
  }

  // Helpers

  iconFromType = (view: CollectionView) => {
    const { types } = this.args.collectionDefinition;
    if (types.length < 2) {
      return undefined;
    }
    return view && ICON_FROM_TYPE[view.collectionType];
  };

  searchValue = (filters: ICollectionFilter[]) => {
    const q = filters.find((filter) => filter.property === 'q');
    return q?.values[0] ?? '';
  };
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    CollectionView: typeof CollectionViewComponent;
    'collection-view': typeof CollectionViewComponent;
  }
}
